import React, { useEffect, useState } from "react";
import { t as T } from "i18next";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError, formatRelative } from "../lib/api";
import Layout from "../components/Layout";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import AuthImage from "../components/AuthImage";
import { useAuth } from "../contexts/AuthContext";
import EditProfileModal from "../components/EditProfileModal";
import PinnedImagesModal from "../components/PinnedImagesModal";
import MyFavoritesList from "../components/MyFavoritesList";
import { TrophyShelf } from "../components/Trophy";
import AgeGateGuard from "../components/AgeGateGuard";
import VipBadge from "../components/VipBadge";
import UserBadges from "../components/UserBadges";
import AgeVerifyBadge from "../components/AgeVerifyBadge";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "../components/ui/dropdown-menu";
import {
  Edit3, MapPin, MessageCircle, UserPlus, UserCheck, MoreHorizontal, Sparkles,
  Image as ImageIcon, Ban, Eye, EyeOff, Trash2, Pin, Heart, Lock, Globe, Users, FolderPlus, Star,
  BadgeCheck, ShieldCheck, Link2,
} from "lucide-react";
import { countryName } from "../lib/countries";
import CountryFlag from "../components/CountryFlag";
import PostComposer from "../components/beta/PostComposer";
import PostFeedList from "../components/beta/PostFeedList";

const PUBLIC_TABS = [
  { k: "profil",     labelKey: "profile.tab_profil"    },
  { k: "gjestebok",  labelKey: "profile.tab_gjestebok" },
  { k: "galleri",    labelKey: "profile.tab_galleri"   },
  { k: "noveller",   labelKey: "profile.tab_noveller"  },
];
const PRIVATE_TABS = [
  { k: "innboks",       labelKey: "profile.tab_innboks",    to: "/meldinger",     badgeKey: "inbox_dm" },
  { k: "grupper",       labelKey: "profile.tab_groups"      },
  { k: "favoritter",    labelKey: "profile.tab_favorites"   },
  { k: "innstillinger", labelKey: "profile.tab_settings",   to: "/innstillinger" },
];
// Mobil-tabs: fjern PROFIL (redundant), INNBOKS (bjelle i topp), INNSTILLINGER (i avatar-meny)
// FAVORITTER droppar "MINE"-prefiks (implisitt fra profil-kontekst), MINE GRUPPER beholder MINE
const MOBILE_PUBLIC_TABS = [
  { k: "gjestebok", labelKey: "profile.tab_gjestebok" },
  { k: "galleri",   labelKey: "profile.tab_galleri"   },
  { k: "noveller",  labelKey: "profile.tab_noveller"  },
];
const MOBILE_PRIVATE_TABS = [
  { k: "grupper",    labelKey: "profile.tab_groups"        },
  { k: "favoritter", labelKey: "profile.tab_favorites_short" },
];

const VIS_ICON = { only_me: Lock, friends: Heart, followers: Users, all: Globe };

export default function BetaProfile() {
  return (
    <AgeGateGuard>
      <BetaProfileInner />
    </AgeGateGuard>
  );
}

function BetaProfileInner() {
  const { t, i18n } = useTranslation();
  const localeMap = { no: "nb-NO", sv: "sv-SE", da: "da-DK", en: "en-GB" };
  const { username } = useParams();
  const { user, refresh } = useAuth();
  const isSelfUrl = !!(user && user.username_lower &&
    user.username_lower === String(username || "").toLowerCase());
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("profil");
  const [editing, setEditing] = useState(false);
  const [pinning, setPinning] = useState(false);
  const [error, setError] = useState("");
  const [blockedInfo, setBlockedInfo] = useState(null); // {code, is_creator, username}
  const [mobilePinnedLightbox, setMobilePinnedLightbox] = useState({ open: false, index: 0 });

  // tab data
  const [albums, setAlbums] = useState([]);
  const [guestbook, setGuestbook] = useState([]);
  const [gbViewerIsOwner, setGbViewerIsOwner] = useState(false);
  const [groups, setGroups] = useState([]);
  const [noveller, setNoveller] = useState([]);
  const [gbBody, setGbBody] = useState("");
  const [visitors, setVisitors] = useState([]);
  const [activity, setActivity] = useState([]);
  const [friends, setFriends] = useState([]);
  const [activityMine, setActivityMine] = useState([]);
  const [trophies, setTrophies] = useState([]);
  const [submenuBadges, setSubmenuBadges] = useState({});
  // Vennskaps-status mot besøkt profil: null | {status: "none"|"friends"|"pending_incoming"|"pending_outgoing"|"self", friendship_id?}
  const [friendStatus, setFriendStatus] = useState(null);
  // Wall-feed reload trigger
  const [postReloadKey, setPostReloadKey] = useState(0);

  // Hent submenu-badges når egen profil er åpen
  useEffect(() => {
    if (!user || user === false) return;
    if (!data || user.username_lower !== data.user.username_lower) return;
    api.get("/users/me/submenu-badges").then((r) => setSubmenuBadges(r.data)).catch(() => {});
  }, [data?.user?.id, user]);

  // Hent vennskaps-status (none/friends/pending_incoming/pending_outgoing) når annen profil åpnes
  useEffect(() => {
    if (!data || !user || user === false) return;
    if (user.username_lower === data.user.username_lower) { setFriendStatus({ status: "self" }); return; }
    api.get(`/friendships/status/${data.user.username}`)
      .then(r => setFriendStatus(r.data))
      .catch(() => setFriendStatus({ status: "none" }));
  }, [data?.user?.id, user]);

  const load = async () => {
    try {
      const r = await api.get(`/users/${username}`);
      setBlockedInfo(null);
      setData(r.data);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // Safety-nett: hvis URL-en peker på meg selv og jeg er innlogget,
      // skal jeg ALDRI bli vist låst/skaper-redirect. Refresh auth + retry én gang.
      if (isSelfUrl && (status === 403 || status === 404)) {
        try {
          await refresh();
          const r2 = await api.get(`/users/${username}`);
          setBlockedInfo(null);
          setData(r2.data);
          return;
        } catch (_) { /* faller gjennom til vanlig feilhåndtering */ }
      }
      if (status === 403 && detail && typeof detail === "object") {
        // Skapere har egen offentlig promo-side – rute besøkende dit
        if (detail.is_creator && detail.username) {
          navigate(`/skaper/${detail.username}`, { replace: true });
          return;
        }
        setBlockedInfo(detail);
      } else if (status === 404) {
        setBlockedInfo({ code: "not_found", message: T("ui.user_not_found") });
      } else {
        setBlockedInfo({ code: "error", message: formatApiError(err) });
      }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [username, isSelfUrl]);

  // Spor besøk når annen profil åpnes (og bruker er logget inn)
  useEffect(() => {
    if (!data || !user || user === false) return;
    if (user.username_lower === data.user.username_lower) return;
    api.post(`/users/${username}/visit`).catch(() => {});
    // eslint-disable-next-line
  }, [data?.user?.id]);

  useEffect(() => {
    // Album hentes uansett på profilen (vises både i profil-tab og galleri-tab)
    if (tab === "profil" || tab === "galleri") {
      api.get(`/albums?owner=${encodeURIComponent(username)}`).then((r)=>setAlbums(r.data)).catch(()=>{});
    }
    if (tab === "gjestebok")  api.get(`/users/${username}/guestbook`).then((r)=>{
      setGuestbook(r.data.entries || []);
      setGbViewerIsOwner(!!r.data.viewer_is_owner);
    }).catch(()=>{});
    if (tab === "grupper")    api.get(`/groups?tab=mine`).then((r)=>setGroups(r.data)).catch(()=>setGroups([]));
    if (tab === "noveller")   api.get(`/users/${username}/noveller`).then((r)=>setNoveller(r.data)).catch(()=>setNoveller([]));
  }, [tab, username]);

  // Hent trofé-skap (vinst fra konkurranser) når profilen er lastet
  useEffect(() => {
    if (!username) return;
    api.get(`/users/${username}/trophies`).then((r) => setTrophies(r.data.trophies || [])).catch(() => setTrophies([]));
  }, [username]);

  // Last besøkende + aktivitet for venstre kolonne (bare hvis vi er eier)
  useEffect(() => {
    if (!data || !user || user === false) return;
    const isOwner = user.username_lower === data.user.username_lower;
    if (tab !== "profil") return;
    // Venner – synlig for alle
    api.get(`/users/${username}/friends?limit=8`).then(r => setFriends(r.data || [])).catch(() => setFriends([]));
    // Offentlig aktivitet – synlig for alle (høyre kolonne)
    api.get(`/users/${username}/activity?limit=12`).then(r => setActivity(r.data || [])).catch(() => setActivity([]));
    // Privat for eier
    if (isOwner) {
      api.get("/me/profile-visitors?limit=8").then(r => setVisitors(r.data || [])).catch(() => {});
      api.get("/me/activity-on-mine?limit=8").then(r => setActivityMine(r.data || [])).catch(() => {});
    }
    // eslint-disable-next-line
  }, [tab, data?.user?.id, username]);

  const sendGuestbook = async (e) => {
    e.preventDefault();
    if (!gbBody.trim()) return;
    try {
      await api.post(`/users/${username}/guestbook`, { body: gbBody });
      setGbBody("");
      const r = await api.get(`/users/${username}/guestbook`);
      setGuestbook(r.data.entries || []);
      setGbViewerIsOwner(!!r.data.viewer_is_owner);
    } catch (err) { /* ignore */ }
  };
  const reloadGuestbook = async () => {
    const r = await api.get(`/users/${username}/guestbook`);
    setGuestbook(r.data.entries || []);
    setGbViewerIsOwner(!!r.data.viewer_is_owner);
  };
  const toggleGuestbookVisibility = async (id) => {
    try { await api.post(`/guestbook/${id}/visibility`); await reloadGuestbook(); } catch (err) { /* */ }
  };
  const deleteGuestbookEntry = async (id) => {
    if (!window.confirm("Slette denne hilsenen?")) return;
    try { await api.delete(`/guestbook/${id}`); await reloadGuestbook(); } catch (err) { /* */ }
  };

  if (blockedInfo) {
    const isPrivate = blockedInfo.code === "profile_private";
    const isFriends = blockedInfo.code === "profile_friends_only";
    const isNotFound = blockedInfo.code === "not_found";
    return (
      <Layout withSidebar={false}>
        <div className="max-w-xl mx-auto py-20 text-center" data-testid="profile-blocked">
          <Lock className="h-10 w-10 text-[var(--cream-faint)] mx-auto mb-5" />
          <h1 className="font-fraunces italic text-4xl text-[var(--cream)] mb-3">
            {isNotFound ? T("ui.profile_not_found") : isFriends ? T("ui.friends_only") : T("ui.private_profile")}
          </h1>
          <p className="text-[var(--cream-soft)] mb-8 leading-relaxed">
            {isNotFound
              ? "Brukernavnet matcher ingen i fellesskapet."
              : isFriends
                ? `@${blockedInfo.username || username} viser profilen sin kun til venner (gjensidig følging).`
                : `@${blockedInfo.username || username} har valgt å holde profilen sin privat.`}
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Link to="/" className="px-5 py-2 border border-white/10 text-[var(--cream)] font-meta hover:border-[var(--magenta)]/40">
              ← Til forsiden
            </Link>
            <Link to="/sok" className="px-5 py-2 border border-[var(--magenta)]/40 text-[var(--magenta)] font-meta hover:bg-[var(--magenta)]/10">
              Finn andre medlemmer →
            </Link>
          </div>
        </div>
      </Layout>
    );
  }
  if (!data) return <Layout><div className="font-meta text-[var(--cream-soft)]">{T("ui.loading")}</div></Layout>;
  const { user: profile, recent_threads, pinned_images = [] } = data;
  const isMe = user && user !== false && user.username_lower === profile.username_lower;
  const isOnline = profile.last_seen && (Date.now() - new Date(profile.last_seen).getTime()) < 10*60*1000;
  const tabs = isMe ? [...PUBLIC_TABS, ...PRIVATE_TABS] : PUBLIC_TABS;

  // Topp-info-strenger
  const identityLine = [
    profile.age && `${profile.age} ${t("profile.yrs")}`,
    profile.show_birthday && profile.birthdate ? new Date(profile.birthdate).toLocaleDateString("nb-NO") : null,
    profile.gender_v3 || (profile.gender ? profile.gender : null),
    profile.pronoun,
  ].filter(Boolean).join(" · ");
  const civilLine = (profile.civil_status_v3 || []).join(" · ");
  const orientationLine = (profile.orientation_v3 || []).join(" · ");
  const seekerLine = (profile.looking_for_pos || []).slice(0, 4).join(", ");

  return (
    <Layout withSidebar={false}>
      {/* TOPP-SEKSJON: 2 kolonner */}
      <div className="viking-band relative">
        {/* MOBIL-VERSJON: avatar + 3 uthevede bilder + Handlinger-drawer + tekstblokk */}
        <div className="sm:hidden pb-6 profile-divider">
          <div className="flex gap-3 mb-4">
            {/* Avatar 160×240 – 2:3 (hvit for visuell verifisering) */}
            <div className="relative shrink-0">
              <div className="relative w-40 h-60 bg-black border border-white/10 overflow-hidden flex items-center justify-center" data-testid="mobile-profile-avatar-box">
                {profile.avatar_url ? (
                  <img src={profile.avatar_url} alt={profile.username} className="w-full h-full object-cover" />
                ) : (
                  <div className="bg-[var(--magenta)] text-black text-5xl font-bold w-full h-full flex items-center justify-center">
                    {profile.username.slice(0,2).toUpperCase()}
                  </div>
                )}
                {isOnline && <span className="absolute right-2 bottom-2 h-4 w-4 rounded-full bg-[var(--magenta)] ring-2 ring-[var(--noir)] online-dot" />}
              </div>
            </div>
            {/* Høyre kolonne: 3 uthevede vannrett øverst, direkte knapper nederst */}
            <div className="flex-1 flex flex-col gap-1.5 min-w-0">
              <div className="grid grid-cols-3 gap-1.5">
                {[0, 1, 2].map((i) => {
                  const img = pinned_images[i];
                  return (
                    <div
                      key={i}
                      className="aspect-[2/3] bg-black border border-white/10 overflow-hidden cursor-pointer"
                      data-testid={`mobile-pinned-slot-${i}`}
                      onClick={() => {
                        if (pinned_images.length > 0) {
                          setMobilePinnedLightbox({ open: true, index: img ? i : 0 });
                        } else if (isMe) {
                          setPinning(true);
                        }
                      }}
                    >
                      {img ? (
                        <AuthImage imageId={img.id} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[var(--cream-faint)]">
                          <ImageIcon className="h-5 w-5" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Direkte handlingsknapper — fyller tomrommet under uthevede bilder */}
              {isMe ? (
                <div className="flex flex-col gap-1.5" data-testid="mobile-actions-inline">
                  <button
                    onClick={() => setEditing(true)}
                    className="btn-ghost w-full text-[11px] py-2 flex items-center justify-center gap-1.5"
                    data-testid="mobile-edit-profile"
                  >
                    <Edit3 className="h-3 w-3" /> {t("profile.edit_profile")}
                  </button>
                  <button
                    onClick={() => navigate("/innstillinger/selger-lenker")}
                    className="btn-ghost w-full text-[11px] py-2 flex items-center justify-center gap-1.5"
                    data-testid="mobile-my-seller-links"
                  >
                    <Link2 className="h-3 w-3" /> Selger-lenker
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-1.5" data-testid="mobile-actions-inline">
                  <button
                    onClick={() => navigate(`/meldinger?til=${profile.username}`)}
                    className="btn-magenta w-full text-[11px] py-2 flex items-center justify-center gap-1.5"
                    data-testid="mobile-send-message"
                  >
                    <MessageCircle className="h-3 w-3" /> {t("profile.send_message")}
                  </button>
                  <div className="grid grid-cols-2 gap-1.5">
                    <button
                      onClick={async () => { try { await api.post(`/users/${profile.username}/follow`); await load(); } catch (e) {} }}
                      className="btn-ghost text-[10px] py-1.5 flex items-center justify-center gap-1"
                      data-testid="mobile-follow"
                    >
                      {profile.is_following
                        ? <><UserCheck className="h-3 w-3" /> {t("profile.following")}</>
                        : <><UserPlus className="h-3 w-3" /> {t("profile.follow")}</>}
                    </button>
                    <button
                      onClick={async () => { try { await api.post(`/users/${profile.username}/favorite`); await load(); } catch (e) {} }}
                      className="btn-ghost text-[10px] py-1.5 flex items-center justify-center gap-1"
                      data-testid="mobile-favorite"
                    >
                      <Star className={`h-3 w-3 ${profile.is_favorited ? "fill-[var(--magenta)] text-[var(--magenta)]" : ""}`} />
                      {t("profile.favorite")}
                    </button>
                    {profile.allow_friend_requests !== false && friendStatus?.status !== "friends" && friendStatus?.status !== "pending_outgoing" ? (
                      <button
                        onClick={async () => {
                          try {
                            await api.post(`/friendships/request/${profile.username}`);
                            const s = await api.get(`/friendships/status/${profile.username}`);
                            setFriendStatus(s.data);
                          } catch (e) {}
                        }}
                        className="btn-ghost text-[10px] py-1.5 flex items-center justify-center gap-1 leading-tight text-center"
                        data-testid="mobile-friend-request"
                      >
                        <UserPlus className="h-3 w-3 shrink-0" />
                        <span>{T("ui.send_friend_request")}</span>
                      </button>
                    ) : (
                      <button
                        disabled
                        className="btn-ghost text-[10px] py-1.5 flex items-center justify-center gap-1 opacity-60"
                        data-testid="mobile-friend-status"
                      >
                        <UserCheck className="h-3 w-3" />
                        {friendStatus?.status === "friends" ? t("profile.friend", "Venn") : t("profile.pending", "Venter")}
                      </button>
                    )}
                    <button
                      onClick={async () => { try { await api.post(`/users/${profile.username}/block`); if (profile.is_blocked) await load(); else navigate("/"); } catch (e) {} }}
                      className="btn-ghost text-[10px] py-1.5 flex items-center justify-center gap-1 text-[var(--magenta)]"
                      data-testid="mobile-block"
                    >
                      <Ban className="h-3 w-3" />
                      {profile.is_blocked ? "Opphev" : "Blokker"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Skaperside-lenke — bred, under topp-rad (avatar+bilder+knapper), før tekstblokk */}
          {profile.is_creator && profile.show_creator_link_on_profile === true && (
            <Link
              to={`/skaper/${profile.username}`}
              data-testid="mobile-creator-link-banner"
              className="mb-4 w-full flex items-center justify-center gap-2 border-2 border-[var(--magenta)] bg-[var(--magenta)]/15 hover:bg-[var(--magenta)]/25 transition-colors px-3 py-2.5"
            >
              <Sparkles className="h-4 w-4 text-[var(--magenta)] shrink-0" />
              <span className="font-fraunces italic text-sm text-[var(--cream)] truncate">
                {(profile.creator_link_text || "").trim() || t("profile.visit_creator_page_default", "Besøk min skaperside")}
              </span>
            </Link>
          )}

          {/* Tekstblokk under bilde-rad: navn+badges, handle+lokasjon+sivilstatus, søker */}
          <div className="min-w-0">
            <h1 className="font-fraunces italic text-[var(--cream)] leading-tight mb-1 inline-flex items-center gap-2 flex-wrap break-words text-2xl" data-testid="mobile-profile-username">
              <span className="break-words">{profile.display_name || profile.username}</span>
              <UserBadges user={profile} size="sm" />
              <AgeVerifyBadge isSelf={isMe} />
              {profile.tier && profile.tier !== "free" && (
                <span className="text-[var(--magenta)] inline-flex items-center gap-1 text-xs font-manrope not-italic uppercase tracking-wider" data-testid="mobile-vip-badge">
                  <Sparkles className="h-3 w-3" /> {profile.tier === "creator" ? t("profile.tier_creator") : t("profile.tier_vip")}
                </span>
              )}
            </h1>
            {/* Én sammenslått linje: @handle · identity · lokasjon · sivilstatus · orientering */}
            <div className="font-manrope text-xs text-[var(--cream-soft)] flex flex-wrap items-center gap-x-2 gap-y-1 mb-1">
              {profile.display_name && <span>@{profile.username}</span>}
              {profile.display_name && identityLine && <span className="text-[var(--cream-faint)]">·</span>}
              {identityLine && <span>{identityLine}</span>}
              {(profile.city || profile.fylke || profile.country) && (
                <>
                  <span className="text-[var(--cream-faint)]">·</span>
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {profile.country && <CountryFlag iso={profile.country} size="sm" />}
                    {[profile.city, profile.fylke].filter(Boolean).join(", ")}
                  </span>
                </>
              )}
              {civilLine && (
                <>
                  <span className="text-[var(--cream-faint)]">·</span>
                  <span>{civilLine}</span>
                </>
              )}
              {orientationLine && (
                <>
                  <span className="text-[var(--cream-faint)]">·</span>
                  <span>{orientationLine}</span>
                </>
              )}
            </div>
            {(profile.tags_v3 || []).length > 0 && (
              <div className="font-manrope text-xs text-[var(--cream)] mt-1 flex flex-wrap gap-x-2 gap-y-0.5" data-testid="mobile-inline-tags">
                <span className="text-[var(--cream-faint)] uppercase tracking-[0.12em] text-[10px]">{T("ui.tags")}</span>
                {profile.tags_v3.map((tg) => (
                  <span key={tg} className="text-[var(--cream-soft)]">#{tg}</span>
                ))}
              </div>
            )}
            {profile.ornaments && (profile.ornaments.has_piercings || profile.ornaments.has_tattoos) && (
              <div className="font-manrope text-xs text-[var(--cream)] mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5" data-testid="mobile-inline-ornaments">
                <span className="text-[var(--cream-faint)] uppercase tracking-[0.12em] text-[10px]">{T("ui.decorations")}</span>
                {profile.ornaments.has_piercings && (
                  <span>{t("profile_v2.piercings")}: <span className="text-[var(--cream-soft)]">{profile.ornaments.piercings_amount || "—"}</span></span>
                )}
                {profile.ornaments.has_piercings && profile.ornaments.has_tattoos && <span className="text-[var(--cream-faint)]">·</span>}
                {profile.ornaments.has_tattoos && (
                  <span>{t("profile_v2.tattoos")}: <span className="text-[var(--cream-soft)]">{profile.ornaments.tattoos_amount || "—"}</span></span>
                )}
              </div>
            )}
          </div>

          {/* Mobil-lightbox for uthevede bilder */}
          {mobilePinnedLightbox.open && pinned_images.length > 0 && (
            <div
              className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
              onClick={() => setMobilePinnedLightbox({ open: false, index: 0 })}
              data-testid="mobile-pinned-lightbox"
            >
              <button
                className="absolute top-4 right-4 text-white/80 hover:text-white"
                onClick={(e) => { e.stopPropagation(); setMobilePinnedLightbox({ open: false, index: 0 }); }}
                aria-label={T("ui.close")}
              >
                <MoreHorizontal className="h-6 w-6 rotate-90" />
              </button>
              <div
                className="w-full h-full overflow-x-auto snap-x snap-mandatory flex"
                onClick={(e) => e.stopPropagation()}
              >
                {pinned_images.map((img, i) => (
                  <div key={img.id} className="snap-center shrink-0 w-screen h-screen flex items-center justify-center px-4" data-index={i}>
                    <Link
                      to={`/albums/${img.album_id}`}
                      onClick={() => setMobilePinnedLightbox({ open: false, index: 0 })}
                    >
                      <AuthImage imageId={img.id} className="max-w-full max-h-full object-contain" />
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* DESKTOP-VERSJON — skjult på mobil */}
        <div className="hidden sm:block pb-8 mb-0 profile-divider">
        <div className={`grid grid-cols-1 ${(isMe || pinned_images.length > 0) ? "sm:grid-cols-[1.6fr_1fr] lg:grid-cols-[1.1fr_1fr]" : ""} gap-4 sm:gap-6 lg:gap-8`}>
        {/* venstre: identitet */}
        <div className="grid grid-cols-[auto_1fr] gap-3 sm:gap-5 items-start">
          <div className="relative shrink-0 self-start flex">
            <div className="relative w-24 h-32 sm:w-44 sm:h-56 lg:w-52 lg:h-64 bg-black border border-white/10 overflow-hidden flex items-center justify-center">
              {profile.avatar_url ? (
                <img src={profile.avatar_url} alt={profile.username} className="w-full h-full object-contain" />
              ) : (
                <div className="bg-[var(--magenta)] text-black text-5xl font-bold w-full h-full flex items-center justify-center">
                  {profile.username.slice(0,2).toUpperCase()}
                </div>
              )}
              {isOnline && <span className="absolute right-2 bottom-2 h-4 w-4 rounded-full bg-[var(--magenta)] ring-2 ring-[var(--noir)] online-dot" />}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-meta mb-2 flex items-center gap-3">
              {isOnline ? t("profile.online") : `${t("profile.active_prefix")} ${formatRelative(profile.last_seen)}`}
              {profile.tier && profile.tier !== "free" && (
                <span className="text-[var(--magenta)] inline-flex items-center gap-1">
                  <Sparkles className="h-3 w-3" /> {profile.tier === "creator" ? t("profile.tier_creator") : t("profile.tier_vip")}
                </span>
              )}
              {profile.role === "admin" && <span className="text-[var(--magenta)]">{t("profile.tier_admin")}</span>}
            </div>
            <h1
              className={`font-fraunces italic text-[var(--cream)] leading-tight mb-1 inline-flex items-center gap-3 flex-wrap break-words ${
                (profile.display_name || profile.username).length > 16
                  ? "text-2xl sm:text-3xl"
                  : (profile.display_name || profile.username).length > 12
                  ? "text-3xl sm:text-4xl"
                  : "text-4xl sm:text-5xl"
              }`}
              data-testid="profile-username"
            >
              <span className="break-words">{profile.display_name || profile.username}</span>
              <UserBadges user={profile} size="lg" />
              <AgeVerifyBadge isSelf={isMe} />
            </h1>
            {profile.display_name && (
              <div className="font-meta mb-2">@{profile.username}</div>
            )}
            <div className="font-manrope text-sm text-[var(--cream-soft)] flex flex-wrap gap-x-4 gap-y-1 mb-2">
              {identityLine && <span>{identityLine}</span>}
              {(profile.city || profile.fylke || profile.country) && (
                <span className="inline-flex items-center gap-1.5" data-testid="profile-location">
                  <MapPin className="h-3.5 w-3.5" />
                  {profile.country && (
                    <CountryFlag iso={profile.country} size="sm" title={countryName(profile.country, (i18n.language || "no").split("-")[0])} data-testid="profile-flag" />
                  )}
                  {[profile.city, profile.fylke].filter(Boolean).join(", ")}
                </span>
              )}
            </div>
            {civilLine && <div className="font-manrope text-xs text-[var(--cream-soft)] mb-1">{civilLine}</div>}
            {orientationLine && <div className="font-manrope text-xs text-[var(--cream-soft)] mb-2">{orientationLine}</div>}
            {seekerLine && (
              <div className="font-manrope text-sm text-[var(--cream)]">
                <span className="text-[var(--cream-faint)]">{t("profile.seeker")}: </span>{seekerLine}
              </div>
            )}
            {/* Knapper – rekkefølge basert på "nærhet": venneforespørsel (sterkest) → følg → favoritt → send melding → mer */}
            <div className="flex gap-2 mt-4 flex-wrap">
              {isMe ? (
                <>
                  <button onClick={()=>setEditing(true)} className="btn-ghost" data-testid="edit-profile-button">
                    <Edit3 className="h-3.5 w-3.5 inline mr-2" /> {t("profile.edit_profile")}
                  </button>
                  <button
                    onClick={() => navigate("/innstillinger/selger-lenker")}
                    className="btn-ghost"
                    data-testid="profile-my-seller-links-btn"
                  >
                    <Link2 className="h-3.5 w-3.5 inline mr-2" /> Mine selger-lenker
                  </button>
                  {profile.is_creator && (
                    <button
                      onClick={() => navigate(`/skaper/${profile.username}`)}
                      data-testid="goto-creator-page-btn"
                      className="px-4 py-2 text-xs uppercase tracking-[0.15em] font-meta bg-[var(--magenta)] text-black hover:bg-[var(--cream)] inline-flex items-center gap-2"
                    >
                      <Sparkles className="h-3.5 w-3.5" /> {t("profile.my_creator_page")}
                    </button>
                  )}
                </>
              ) : (
                <>
                  {/* 1. Venneforespørsel – sterkest tilknytning, nærmest profil */}
                  {friendStatus?.status === "pending_incoming" && friendStatus.friendship_id ? (
                    <>
                      <button
                        onClick={async () => {
                          try {
                            await api.post(`/friendships/${friendStatus.friendship_id}/accept`);
                            try { (await import("sonner")).toast.success(T("ui.friend_accepted")); } catch {}
                            const s = await api.get(`/friendships/status/${profile.username}`);
                            setFriendStatus(s.data);
                            await load();
                          } catch (e) {
                            const detail = e?.response?.data?.detail || T("ui.accept_failed");
                            try { (await import("sonner")).toast.error(detail); } catch { alert(detail); }
                          }
                        }}
                        className="btn-magenta"
                        data-testid="friend-accept-button"
                      >
                        <UserCheck className="h-3.5 w-3.5 inline mr-2" /> Godta venneforespørsel
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            await api.post(`/friendships/${friendStatus.friendship_id}/reject`);
                            try { (await import("sonner")).toast.success(T("ui.friend_declined")); } catch {}
                            const s = await api.get(`/friendships/status/${profile.username}`);
                            setFriendStatus(s.data);
                          } catch (e) {
                            const detail = e?.response?.data?.detail || T("ui.decline_failed");
                            try { (await import("sonner")).toast.error(detail); } catch { alert(detail); }
                          }
                        }}
                        className="btn-ghost"
                        data-testid="friend-reject-button"
                      >
                        Avslå
                      </button>
                    </>
                  ) : friendStatus?.status === "pending_outgoing" ? (
                    <button
                      disabled
                      className="btn-ghost opacity-60 cursor-not-allowed"
                      data-testid="friend-request-sent"
                      title={T("ui.friend_sent_title")}
                    >
                      <UserCheck className="h-3.5 w-3.5 inline mr-2" /> Forespørsel sendt
                    </button>
                  ) : friendStatus?.status === "friends" ? (
                    <span className="btn-ghost cursor-default" data-testid="friend-already" title={T("ui.friends_title")}>
                      <UserCheck className="h-3.5 w-3.5 inline mr-2" /> {T("ui.friends")}
                    </span>
                  ) : profile.allow_friend_requests !== false && !profile.is_friend ? (
                    <button
                      onClick={async () => {
                        try {
                          await api.post(`/friendships/request/${profile.username}`);
                          try { (await import("sonner")).toast.success(T("ui.friend_request_sent")); } catch {}
                          const s = await api.get(`/friendships/status/${profile.username}`);
                          setFriendStatus(s.data);
                          await load();
                        } catch (e) {
                          const detail = e?.response?.data?.detail || T("ui.send_failed2");
                          try { (await import("sonner")).toast.error(detail); }
                          catch { alert(detail); }
                        }
                      }}
                      className="btn-ghost"
                      data-testid="friend-request-button"
                    >
                      <UserPlus className="h-3.5 w-3.5 inline mr-2" /> {t("profile.send_friend_request")}
                    </button>
                  ) : null}
                  {/* 2. Følg */}
                  <button onClick={async () => { try { await api.post(`/users/${profile.username}/follow`); await load(); } catch (e) {} }}
                    className="btn-ghost" data-testid="follow-button">
                    {profile.is_following ? <><UserCheck className="h-3.5 w-3.5 inline mr-2" /> {t("profile.following")}</>
                                          : <><UserPlus className="h-3.5 w-3.5 inline mr-2" /> {t("profile.follow")}</>}
                  </button>
                  {/* 3. Favoritt (privat) */}
                  <button
                    onClick={async () => {
                      try { await api.post(`/users/${profile.username}/favorite`); await load(); } catch (e) {}
                    }}
                    className="btn-ghost"
                    data-testid="favorite-button"
                    title={profile.is_favorited ? "Fjern favoritt" : "Legg til favoritt (privat)"}
                  >
                    <Star
                      className={`h-3.5 w-3.5 inline mr-2 ${profile.is_favorited ? "fill-[var(--magenta)] text-[var(--magenta)]" : ""}`}
                    />
                    {t("profile.favorite")}
                  </button>
                  {/* 4. Send melding */}
                  <button onClick={() => navigate(`/meldinger?til=${profile.username}`)} className="btn-magenta" data-testid="msg-button">
                    <MessageCircle className="h-3.5 w-3.5 inline mr-2" /> {t("profile.send_message")}
                  </button>
                  {/* Skaperside-knappen er flyttet til synlig banner under fremhevede bilder */}
                  {/* 6. Mer-meny: blokker */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="btn-ghost" data-testid="more-button" aria-label={T("ui.more_options")}><MoreHorizontal className="h-3.5 w-3.5" /></button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-[var(--noir-elev)] border-white/10 text-[var(--cream)] w-56 rounded-sm">
                      <DropdownMenuItem onClick={async () => { try { await api.post(`/users/${profile.username}/block`); if (profile.is_blocked) await load(); else navigate("/"); } catch (e) {} }}
                        className="text-[var(--cream)] font-medium border-l-2 border-[var(--magenta)] pl-3 text-xs uppercase tracking-[0.15em]" data-testid="block-button">
                        <Ban className="h-3.5 w-3.5 mr-2" />
                        {profile.is_blocked ? "Opphev blokkering" : "Blokker bruker"}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              )}
            </div>
          </div>
        </div>

        {/* høyre: 5 pinned bilder (skjul tom seksjon for gjester) */}
        {(isMe || pinned_images.length > 0) && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="font-meta">{t("profile_v2.pinned_images")}</div>
            {isMe && (
              <button onClick={() => setPinning(true)} className="font-meta text-[var(--magenta)] hover:underline inline-flex items-center gap-1.5" data-testid="pin-button">
                <Pin className="h-3 w-3" /> {pinned_images.length > 0 ? t("profile_v2.change") : t("profile_v2.choose_5")}
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5">
            {Array.from({ length: 5 }).map((_, i) => {
              const img = pinned_images[i];
              return (
                <div key={i} className="aspect-[3/4] bg-[var(--noir-elev)] border border-white/10 overflow-hidden">
                  {img ? (
                    <Link to={`/albums/${img.album_id}`}>
                      <AuthImage imageId={img.id} className="w-full h-full object-cover hover:scale-105 transition-transform" />
                    </Link>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[var(--cream-faint)]">
                      <ImageIcon className="h-5 w-5" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Banner: lenke til skaperside (kun hvis bruker er skaper og har valgt å vise) — DESKTOP */}
          {profile.is_creator && profile.show_creator_link_on_profile === true && (
            <Link
              to={`/skaper/${profile.username}`}
              data-testid="creator-link-banner"
              className="hidden sm:block mt-4 group relative overflow-hidden border-2 border-[var(--magenta)]/60 bg-gradient-to-r from-[var(--magenta)]/20 via-[var(--magenta)]/10 to-transparent hover:border-[var(--magenta)] hover:from-[var(--magenta)]/30 transition-all"
            >
              <div className="flex items-center justify-between gap-4 px-5 py-4">
                <div className="flex items-center gap-3 min-w-0">
                  <Sparkles className="h-5 w-5 text-[var(--magenta)] shrink-0" />
                  <span className="font-fraunces italic text-lg text-[var(--cream)] truncate">
                    {(profile.creator_link_text || "").trim() || t("profile.visit_creator_page_default", "Besøk min skaperside")}
                  </span>
                </div>
                <span className="font-meta text-[10px] tracking-[0.25em] text-[var(--magenta)] uppercase shrink-0 group-hover:translate-x-1 transition-transform">
                  →
                </span>
              </div>
            </Link>
          )}
        </div>
        )}
        </div>
        </div>
      </div>

      {/* TABS – DESKTOP */}
      <div className="mb-10 profile-tabs-bar hidden sm:block">
        <div className="flex gap-6 flex-wrap">
        {PUBLIC_TABS.map((tb) => (
          <button key={tb.k} onClick={()=>setTab(tb.k)} data-testid={`tab-${tb.k}`}
            className={`pb-3 font-manrope text-[11px] uppercase tracking-[0.25em] whitespace-nowrap transition-colors relative ${
              tab === tb.k ? "text-[var(--cream)]" : "text-[var(--cream-soft)] hover:text-[var(--cream)]"
            }`}>
            {t(tb.labelKey)}
            {tab === tb.k && <span className="absolute left-0 right-0 -bottom-px h-px bg-[var(--magenta)]" />}
          </button>
        ))}
        {isMe && <span className="self-center text-[var(--cream-faint)] text-xs">|</span>}
        {isMe && PRIVATE_TABS.map((tb) => {
          const isLink = !!tb.to;
          const onClick = () => { if (isLink) navigate(tb.to); else setTab(tb.k); };
          const badge = tb.badgeKey ? (submenuBadges[tb.badgeKey] || 0) : 0;
          // Spesial-badge for grupper-fanen: friend_requests vises også der via innstillinger
          return (
            <button key={tb.k} onClick={onClick} data-testid={`tab-${tb.k}`}
              className={`pb-3 font-manrope text-[11px] uppercase tracking-[0.25em] whitespace-nowrap transition-colors relative inline-flex items-center gap-1.5 ${
                tab === tb.k ? "text-[var(--cream)]" : "text-[var(--cream-soft)] hover:text-[var(--cream)]"
              }`}>
              <Lock className="h-2.5 w-2.5" /> {t(tb.labelKey)}
              {badge > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-semibold bg-[var(--magenta)] text-white rounded-full" data-testid={`badge-${tb.k}`}>
                  {badge > 99 ? "99+" : badge}
                </span>
              )}
              {tab === tb.k && <span className="absolute left-0 right-0 -bottom-px h-px bg-[var(--magenta)]" />}
            </button>
          );
        })}
        </div>
      </div>

      {/* TABS – MOBIL (5 stk: gjestebok, galleri, noveller, mine grupper, favoritter) */}
      <div className="mb-3 profile-tabs-bar sm:hidden">
        <div className="flex gap-3 overflow-x-auto pb-1" style={{ scrollbarWidth: "none" }} data-testid="mobile-tabs">
          {MOBILE_PUBLIC_TABS.map((tb) => (
            <button key={tb.k} onClick={() => setTab(tb.k)} data-testid={`mobile-tab-${tb.k}`}
              className={`py-1.5 font-manrope text-[10px] uppercase tracking-[0.2em] whitespace-nowrap transition-colors relative shrink-0 ${
                tab === tb.k ? "text-[var(--cream)]" : "text-[var(--cream-soft)]"
              }`}>
              {t(tb.labelKey)}
              {tab === tb.k && <span className="absolute left-0 right-0 -bottom-px h-px bg-[var(--magenta)]" />}
            </button>
          ))}
          {isMe && MOBILE_PRIVATE_TABS.map((tb) => {
            const badge = tb.badgeKey ? (submenuBadges[tb.badgeKey] || 0) : 0;
            return (
              <button key={tb.k} onClick={() => setTab(tb.k)} data-testid={`mobile-tab-${tb.k}`}
                className={`py-1.5 font-manrope text-[10px] uppercase tracking-[0.2em] whitespace-nowrap transition-colors relative inline-flex items-center gap-1 shrink-0 ${
                  tab === tb.k ? "text-[var(--cream)]" : "text-[var(--cream-soft)]"
                }`}>
                <Lock className="h-2.5 w-2.5" /> {t(tb.labelKey)}
                {badge > 0 && (
                  <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 text-[9px] font-semibold bg-[var(--magenta)] text-white rounded-full">
                    {badge > 99 ? "99+" : badge}
                  </span>
                )}
                {tab === tb.k && <span className="absolute left-0 right-0 -bottom-px h-px bg-[var(--magenta)]" />}
              </button>
            );
          })}
        </div>
      </div>
      {tab === "profil" && (
        <div className="viking-band relative">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr_1fr] gap-3 lg:gap-8">
          {/* VENSTRE – Aktivitet + Besøkende + Venner + Tags + Trofeer + Smykker */}
          <aside className="space-y-4">
            {/* MOBIL: BESØKENDE + AKTIVITET side-om-side */}
            {isMe && (
              <div className="grid grid-cols-2 gap-2 sm:hidden" data-testid="mobile-visitors-activity">
                <div className="panel-private p-3">
                  <div className="panel-heading text-[10px]" style={{ color: "var(--gold)" }}>
                    <Lock className="h-2.5 w-2.5" /> {t("profile_v2.recent_visitors")}
                  </div>
                  {visitors.length === 0 ? (
                    <div className="font-fraunces italic text-[var(--cream-faint)] text-xs">{t("profile_v2.no_visits")}</div>
                  ) : (
                    <ul className="space-y-1.5 mt-2">
                      {visitors.slice(0, 5).map((v) => (
                        <li key={v.user.id} className="flex items-center justify-between gap-1 min-w-0">
                          <Link to={`/profile/${v.user.username}`} className="font-manrope text-[11px] text-[var(--cream)] truncate">
                            {v.user.username}
                          </Link>
                          <span className="font-meta text-[9px] whitespace-nowrap text-[var(--cream-soft)]">{formatRelative(v.last_visit)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {activity.length > 0 && (
                  <div className="panel p-3">
                    <div className="panel-heading text-[10px]" style={{ color: "var(--gold)" }}>{t("profile.panel_activity")}</div>
                    <ul className="space-y-1.5 mt-2">
                      {activity.slice(0, 5).map((it, i) => (
                        <li key={i} className="font-manrope text-[11px] leading-snug min-w-0">
                          <div className="text-[var(--cream)] line-clamp-1 break-words">{it.summary}</div>
                          <div className="font-meta text-[9px] text-[var(--cream-soft)]">{formatRelative(it.created_at)}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            {isMe && (
              <>
                <div className="panel-private hidden sm:block">
                  <div className="panel-heading" style={{ color: "var(--gold)" }}>
                    <Lock className="h-3 w-3" /> {t("profile_v2.recent_visitors")}
                  </div>
                  {visitors.length === 0 ? (
                    <div className="font-fraunces italic text-[var(--cream-faint)] text-sm">{t("profile_v2.no_visits")}</div>
                  ) : (
                    <ul className="space-y-2" data-testid="visitors-list">
                      {visitors.map((v) => (
                        <li key={v.user.id} className="flex items-center justify-between gap-2">
                          <Link to={`/profile/${v.user.username}`} className="font-manrope text-sm text-[var(--cream)] hover:text-[var(--gold)]">
                            {v.user.username}
                          </Link>
                          <span className="font-meta text-[10px]">{formatRelative(v.last_visit)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {activityMine.length > 0 && (
                  <div className="panel-private">
                    <div className="panel-heading" style={{ color: "var(--gold)" }}>
                      <Lock className="h-3 w-3" /> {t("profile_v2.my_activity")}
                    </div>
                    <ul className="space-y-2.5" data-testid="activity-mine-list">
                      {activityMine.slice(0, 6).map((it, i) => (
                        <li key={i} className="font-manrope text-xs leading-snug">
                          {it.actor && (
                            <Link to={`/profile/${it.actor.username}`} className="text-[var(--cream)] hover:text-[var(--gold)] font-medium">
                              {it.actor.username}
                            </Link>
                          )}{" "}
                          <span className="text-[var(--cream-soft)]">{it.summary}</span>
                          <div className="font-meta text-[10px] mt-0.5">{formatRelative(it.created_at)}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
            {activity.length > 0 && (
              <div className="panel hidden sm:block">
                <div className="panel-heading" style={{ color: "var(--gold)" }}>{t("profile.panel_activity")}</div>
                <ul className="space-y-2.5" data-testid="activity-feed">
                  {activity.slice(0, 8).map((it, i) => {
                    let to = null;
                    if (it.target_type === "thread") to = `/thread/${it.target_id}`;
                    else if (it.target_type === "novelle") to = `/noveller/${it.target_id}`;
                    else if (it.target_type === "image") to = it.album_id ? `/albums/${it.album_id}` : null;
                    else if (it.target_type === "group") to = `/grupper/${it.target_id}`;
                    else if (it.target_type === "user" && it.target_username) to = `/profile/${it.target_username}`;
                    const Inner = (
                      <>
                        <span className="text-[var(--cream)]">{it.summary}</span>
                        <div className="font-meta text-[10px] mt-0.5">{formatRelative(it.created_at)}</div>
                      </>
                    );
                    return (
                      <li key={i} className="font-manrope text-xs leading-snug border-b border-white/5 pb-2 last:border-0 last:pb-0">
                        {to ? <Link to={to} className="hover:text-[var(--gold)] block">{Inner}</Link> : Inner}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            {friends.length > 0 && (
              <div className="panel hidden sm:block">
                <div className="panel-heading text-[var(--cream-soft)]">{isMe ? t("profile_v2.my_friends") : t("profile_v2.users_friends", { user: profile.username })}</div>
                <div className="grid grid-cols-4 gap-1.5" data-testid="friends-grid">
                  {friends.slice(0, 8).map((fr) => (
                    <Link key={fr.id} to={`/profile/${fr.username}`} title={fr.username}
                      className="aspect-square panel-card hover:border-[var(--gold)]/50 transition-colors flex items-center justify-center font-manrope text-[10px] text-[var(--cream)] uppercase overflow-hidden">
                      {fr.avatar_url ? (
                        <img src={fr.avatar_url} alt={fr.username} className="w-full h-full object-cover" />
                      ) : fr.username.slice(0,2)}
                    </Link>
                  ))}
                </div>
              </div>
            )}
            {(profile.tags_v3 || []).length > 0 && (
              <div className="panel hidden sm:block">
                <div className="panel-heading text-[var(--cream-soft)]">{t("profile_v2.tags")}</div>
                <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                  {profile.tags_v3.map(tg => (
                    <span key={tg} className="font-manrope text-[var(--cream-soft)] text-sm">#{tg}</span>
                  ))}
                </div>
              </div>
            )}
            {trophies.length > 0 && <div className="hidden sm:block"><TrophyShelf trophies={trophies} compact /></div>}
            {profile.ornaments && (profile.ornaments.has_piercings || profile.ornaments.has_tattoos) && (
              <div className="panel hidden sm:block">
                <div className="panel-heading text-[var(--cream-soft)]">{t("profile_v2.ornaments")}</div>
                <div className="font-manrope text-sm text-[var(--cream)] space-y-1.5">
                  {profile.ornaments.has_piercings && <div>{t("profile_v2.piercings")}: <span className="text-[var(--cream-soft)]">{profile.ornaments.piercings_amount || "—"}</span></div>}
                  {profile.ornaments.has_tattoos   && <div>{t("profile_v2.tattoos")}: <span className="text-[var(--cream-soft)]">{profile.ornaments.tattoos_amount || "—"}</span></div>}
                  {profile.ornaments.note && <div className="font-fraunces italic text-[var(--cream-soft)] mt-2">«{profile.ornaments.note}»</div>}
                </div>
              </div>
            )}
          </aside>

          {/* MIDTEN – Bio + Album */}
          <div className="space-y-4">
            {profile.bio_short && (
              <div className="panel" style={{ borderLeftWidth: 2, borderLeftColor: "var(--gold)" }}>
                <p className="font-fraunces italic text-xl text-[var(--cream)] leading-relaxed">
                  {profile.bio_short}
                </p>
              </div>
            )}
            {(profile.bio_long || profile.bio) && (
              <div className="panel">
                <div className="panel-heading text-[var(--cream-soft)]">{t("profile_v2.about_me")}</div>
                <p className="font-fraunces text-[var(--cream)]/90 leading-[1.7] whitespace-pre-wrap">
                  {profile.bio_long || profile.bio}
                </p>
              </div>
            )}
            {!profile.bio_short && !profile.bio_long && !profile.bio && isMe && (
              <div className="panel font-fraunces italic text-[var(--cream-faint)]">
                {t("profile_v2.empty_bio_hint")}
              </div>
            )}

            {/* WALL-FEED: PostComposer (kun egen profil) + PostFeedList */}
            <div data-testid="beta-profile-wall">
              {isMe && (
                <PostComposer onPosted={() => setPostReloadKey((k) => k + 1)} />
              )}
              <PostFeedList userId={profile.id} reloadKey={postReloadKey} />
            </div>

            {/* Selger-lenker (kun verifiserte selgere) - vises mellom bio og album */}
            {profile.seller_links && profile.seller_links.length > 0 && (
              <div className="space-y-2" data-testid="profile-seller-links">
                <div className="panel-heading text-[var(--cream-soft)] mb-2">{T("ui.links")}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {profile.seller_links
                    .filter((l) => isMe || l.status === "approved")
                    .map((link) => (
                      <a
                        key={link.id}
                        href={link.url}
                        target="_blank"
                        rel="noreferrer nofollow"
                        className="panel-card p-4 flex items-center gap-3 hover:bg-[var(--magenta)]/10 transition-colors"
                        data-testid={`profile-seller-link-${link.id}`}
                      >
                        <div className="h-10 w-10 bg-[var(--magenta)]/20 flex items-center justify-center shrink-0">
                          <span className="text-[var(--magenta)] text-lg">↗</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-[var(--cream)] truncate">{link.label}</div>
                          {isMe && link.status !== "approved" && (
                            <div className="font-meta text-[9px] tracking-[0.15em] uppercase text-yellow-400 mt-0.5">
                              {link.status === "pending" ? "Venter på godkjenning" : "Avvist"}
                            </div>
                          )}
                        </div>
                      </a>
                    ))}
                </div>
              </div>
            )}

            {/* ALBUM-GRID flyttet til høyre-sidebar (Fase 2A v191) */}

            {/* MOBIL: Mine venner — flyttet ned under album */}
            {friends.length > 0 && (
              <div className="panel sm:hidden" data-testid="mobile-friends">
                <div className="panel-heading text-[var(--cream-soft)]">{isMe ? t("profile_v2.my_friends") : t("profile_v2.users_friends", { user: profile.username })}</div>
                <div className="grid grid-cols-4 gap-1.5">
                  {friends.slice(0, 8).map((fr) => (
                    <Link key={fr.id} to={`/profile/${fr.username}`} title={fr.username}
                      className="aspect-square panel-card hover:border-[var(--gold)]/50 transition-colors flex items-center justify-center font-manrope text-[10px] text-[var(--cream)] uppercase overflow-hidden">
                      {fr.avatar_url ? (
                        <img src={fr.avatar_url} alt={fr.username} className="w-full h-full object-cover" />
                      ) : fr.username.slice(0,2)}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* MOBIL: NØKKELTALL + TROFESKAP side-om-side (nederst) */}
            <div className="grid grid-cols-2 gap-2 sm:hidden" data-testid="mobile-stats-trophies">
              <div className="panel p-3">
                <dl className="space-y-1 font-manrope text-[11px]">
                  <div className="flex justify-between">
                    <dt className="text-[var(--cream-soft)]">{t("profile.stat_followers")}</dt>
                    <dd className="text-[var(--cream)]">{profile.follower_count || 0}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-[var(--cream-soft)]">{t("profile.stat_following")}</dt>
                    <dd className="text-[var(--cream)]">{profile.following_count || 0}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-[var(--cream-soft)]">{t("profile.stat_posts")}</dt>
                    <dd className="text-[var(--cream)]">{profile.post_count || 0}</dd>
                  </div>
                </dl>
              </div>
              {trophies.length > 0 ? (
                <div className="panel p-3">
                  <div className="panel-heading text-[10px] text-[var(--cream-soft)]">{T("ui.trophies")}</div>
                  <div className="text-[9px] text-[var(--cream-soft)] mt-1">på pallen {trophies.length}</div>
                  <div className="flex items-center gap-3 mt-2 font-manrope text-[11px]">
                    {(() => {
                      const gold = trophies.filter((tr) => tr.place === 1).length;
                      const silver = trophies.filter((tr) => tr.place === 2).length;
                      const bronze = trophies.filter((tr) => tr.place === 3).length;
                      return (
                        <>
                          {gold > 0 && <span style={{ color: "#F5C542" }}>🥇 {gold}</span>}
                          {silver > 0 && <span style={{ color: "#C0C0C0" }}>🥈 {silver}</span>}
                          {bronze > 0 && <span style={{ color: "#CD7F32" }}>🥉 {bronze}</span>}
                        </>
                      );
                    })()}
                  </div>
                </div>
              ) : (
                <div className="panel p-3 flex items-center justify-center">
                  <span className="font-fraunces italic text-[var(--cream-faint)] text-xs text-center">{T("ui.no_trophies")}</span>
                </div>
              )}
            </div>
          </div>

          {/* HØYRE – ALBUM-CARD (flyttet opp) + Språk + Nøkkeltall + Detaljer + Ser etter + Kontakt + Lek/lyst */}
          <aside className="space-y-4">
            {/* ALBUM-CARD: link til Galleri-fane, viser cover-strip hvis album finnes */}
            <div className="panel hidden sm:block" data-testid="beta-profile-album-card">
              <div className="panel-heading text-[var(--cream-soft)] flex items-center justify-between mb-3">
                <span>{t("creator.albums", "Album")}</span>
                <button onClick={() => setTab("galleri")} className="text-[10px] underline normal-case tracking-normal text-[var(--magenta)]" data-testid="beta-profile-see-all-albums">
                  {t("creator.see_all", "Se alle")}
                </button>
              </div>
              {albums.length === 0 ? (
                <div className="text-center py-4" data-testid="beta-profile-album-empty">
                  <ImageIcon className="h-6 w-6 text-[var(--cream-faint)] mx-auto mb-2 opacity-60" />
                  <p className="font-fraunces italic text-[var(--cream-soft)] text-xs">
                    {isMe ? t("album.no_albums", T("ui.no_albums")) : t("profile.no_albums", "Ingen album")}
                  </p>
                  {isMe && (
                    <Link to="/albums" className="text-[10px] underline text-[var(--magenta)] mt-1 inline-block">
                      {t("album.create_new", "Lag album")}
                    </Link>
                  )}
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-1.5" data-testid="beta-profile-album-grid">
                    {albums.slice(0, 6).map((a) => (
                      <Link key={a.id} to={`/albums/${a.id}`} className="group block overflow-hidden bg-[var(--noir-elev)] aspect-square relative" data-testid={`beta-profile-album-${a.id}`}>
                        {a.cover_image_id ? (
                          <AuthImage
                            imageId={a.cover_image_id}
                            alt={a.title}
                            className="w-full h-full object-cover transition duration-500 group-hover:scale-105"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[var(--cream-faint)]">
                            <ImageIcon className="h-5 w-5 opacity-50" />
                          </div>
                        )}
                      </Link>
                    ))}
                  </div>
                  <button
                    onClick={() => setTab("galleri")}
                    className="mt-3 w-full text-center text-[10px] font-meta tracking-[0.2em] uppercase py-2 border border-[var(--magenta)]/40 text-[var(--magenta)] hover:bg-[var(--magenta)]/10 transition-colors"
                    data-testid="beta-profile-album-cta"
                  >
                    {t("profile.see_all_albums", { count: albums.length, defaultValue: "Se alle album ({{count}})" }).replace("{{count}}", albums.length)}
                  </button>
                </>
              )}
            </div>

            {(profile.languages_v3 || []).length > 0 && (
              <div className="panel hidden sm:block">
                <div className="panel-heading text-[var(--cream-soft)]">{t("profile_v2.languages")}</div>
                <div className="flex flex-wrap gap-1.5">
                  {profile.languages_v3.map(l => (
                    <span key={l} className="px-2 py-0.5 rounded-sm border border-white/10 text-[var(--cream-soft)] text-[11px] font-manrope">{l}</span>
                  ))}
                </div>
              </div>
            )}
            {(() => {
              // Skjul enkelt-panelet på mobil hvis mobil-2-kol brukes for nokkeltall+trofeskap
              return null;
            })()}
            <div className="panel hidden sm:block">
              <div className="panel-heading text-[var(--cream-soft)]">{t("profile.panel_stats")}</div>
              <dl className="space-y-2 font-manrope text-sm">
                <div className="flex justify-between border-b border-white/5 pb-1.5">
                  <dt className="text-[var(--cream-soft)]">{t("profile.stat_followers")}</dt>
                  <dd className="text-[var(--cream)]" data-testid="follower-count">{profile.follower_count || 0}</dd>
                </div>
                <div className="flex justify-between border-b border-white/5 pb-1.5">
                  <dt className="text-[var(--cream-soft)]">{t("profile.stat_following")}</dt>
                  <dd className="text-[var(--cream)]">{profile.following_count || 0}</dd>
                </div>
                <div className="flex justify-between border-b border-white/5 pb-1.5">
                  <dt className="text-[var(--cream-soft)]">{t("profile.stat_posts")}</dt>
                  <dd className="text-[var(--cream)]">{profile.post_count || 0}</dd>
                </div>
                <div className="flex justify-between pb-1.5">
                  <dt className="text-[var(--cream-soft)]">{t("profile.stat_member_since")}</dt>
                  <dd className="text-[var(--cream)]">
                    {profile.created_at ? new Date(profile.created_at).toLocaleDateString(localeMap[i18n.language] || "nb-NO", { year:"numeric", month:"short" }) : "—"}
                  </dd>
                </div>
              </dl>
            </div>
            {(() => {
              const details = [
                [t("profile_v2.det_gender"),      profile.gender_v3 || profile.gender],
                [t("profile_v2.det_pronoun"),     profile.pronoun],
                [t("profile_v2.det_civil"),       (profile.civil_status_v3 || []).join(", ")],
                [t("profile_v2.det_orientation"), (profile.orientation_v3 || []).join(", ")],
                [t("profile_v2.det_body"),        profile.body_type_v3 || profile.body_type],
                [t("profile_v2.det_height"),      profile.height_cm ? `${profile.height_cm} cm` : null],
                [t("profile_v2.det_weight"),      profile.weight_kg ? `${profile.weight_kg} kg` : null],
                [t("profile_v2.det_ethnicity"),   profile.ethnicity],
                [t("profile_v2.det_hair"),        profile.hair_color],
                [t("profile_v2.det_eyes"),        profile.eye_color],
                [t("profile_v2.det_smoking"),     profile.smoking],
              ].filter(([_k, v]) => v);
              if (details.length === 0) return null;
              return (
                <div className="panel hidden sm:block">
                  <div className="panel-heading text-[var(--cream-soft)]">{t("profile.panel_details")}</div>
                  <dl className="space-y-2 font-manrope text-sm">
                    {details.map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-3 border-b border-white/5 pb-1.5 last:border-0">
                        <dt className="text-[var(--cream-soft)]">{k}</dt>
                        <dd className="text-[var(--cream)] text-right">{v}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              );
            })()}
            {((profile.looking_for_pos || []).length > 0 || (profile.looking_for_neg || []).length > 0) && (
              <div className="panel space-y-5">
                {(profile.looking_for_pos || []).length > 0 && (
                  <div>
                    <div className="panel-heading text-[var(--cream-soft)]">{t("profile_v2.looking_for")}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {profile.looking_for_pos.map(tg => (
                        <span key={tg} className="px-2.5 py-1 rounded-sm border text-[var(--cream)] text-[11px] font-manrope" style={{ borderColor: "color-mix(in srgb, var(--gold) 45%, transparent)", background: "color-mix(in srgb, var(--gold) 10%, transparent)" }}>{tg}</span>
                      ))}
                    </div>
                  </div>
                )}
                {(profile.looking_for_neg || []).length > 0 && (
                  <div>
                    <div className="panel-heading text-[var(--cream-soft)]">{t("profile_v2.looking_not_for")}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {profile.looking_for_neg.map(tg => (
                        <span key={tg} className="px-2.5 py-1 rounded-sm border border-red-500/40 bg-red-500/10 text-[var(--cream)] text-[11px] font-manrope">{tg}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {(profile.contact_with_pos || []).length > 0 && (
              <div className="panel">
                <div className="panel-heading text-[var(--cream-soft)]">{T("ui.wants_contact")}</div>
                <div className="font-manrope text-sm text-[var(--cream)]">
                  {profile.contact_with_pos.join(", ")}
                  {profile.age_range && (profile.age_range.min || profile.age_range.max) && (
                    <div className="text-[var(--cream-soft)] mt-1">
                      Alder: {profile.age_range.min || 18}–{profile.age_range.max || 99}
                    </div>
                  )}
                </div>
              </div>
            )}
            {profile.lust_enabled && profile.lust_map && Object.keys(profile.lust_map).length > 0 && (
              <div className="panel">
                <div className="panel-heading text-[var(--cream-soft)]">{t("profile_v2.play_desire")}</div>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(profile.lust_map).map(([k, v]) => {
                    const cls = v === "liker" ? "border-[var(--magenta)]/40 bg-[var(--magenta)]/10"
                              : v === "nysgjerrig" ? "border-amber-400/50 bg-amber-400/10"
                              : "border-red-500/40 bg-red-500/10";
                    const ico = v === "liker" ? "💗" : v === "nysgjerrig" ? "🤔" : "🚫";
                    return <span key={k} className={`px-2.5 py-1 rounded-sm border text-[11px] font-manrope text-[var(--cream)] ${cls}`}>{ico} {k}</span>;
                  })}
                </div>
              </div>
            )}
          </aside>
        </div>
        </div>
      )}

      {/* GJESTEBOK */}
      {tab === "gjestebok" && (
        <div className="max-w-2xl">
          {user && user !== false && !isMe && (
            <form onSubmit={sendGuestbook} className="mb-8">
              <textarea value={gbBody} onChange={(e)=>setGbBody(e.target.value)} rows={3}
                        placeholder={`Skriv en hilsen til ${profile.username}…`}
                        className="input-editorial mb-3" data-testid="guestbook-input" />
              <div className="flex items-center justify-between gap-3">
                <p className="font-meta text-[10px] text-[var(--cream-faint)] max-w-md">
                  Hilsenen sendes til {profile.username}. Den blir synlig for andre først når
                  {" "}{profile.username} velger å vise den.
                </p>
                <button className="btn-magenta whitespace-nowrap" data-testid="guestbook-submit">{T("ui.send_greeting")}</button>
              </div>
            </form>
          )}
          {gbViewerIsOwner && guestbook.some((e) => !e.is_public) && (
            <div className="mb-6 p-3 border border-[var(--magenta)]/30 bg-[var(--magenta)]/[0.05] rounded-sm font-meta text-xs text-[var(--cream-soft)]" data-testid="guestbook-pending-banner">
              {T("ui.greetings_pending", { count: guestbook.filter((e)=>!e.is_public).length })}
            </div>
          )}
          {guestbook.length === 0 ? (
            <div className="font-fraunces italic text-[var(--cream-soft)]">{T("ui.no_greetings")}</div>
          ) : (
            <ul className="space-y-6">
              {guestbook.map((e) => (
                <li key={e.id}
                  className={`border-b border-white/5 pb-5 ${gbViewerIsOwner && !e.is_public ? "opacity-70" : ""}`}
                  data-testid={`guestbook-entry-${e.id}`}>
                  <div className="font-meta mb-2 flex items-center gap-2 flex-wrap">
                    <Link to={`/profile/${e.actor.username}`} className="text-[var(--cream)] hover:text-[var(--magenta)]">
                      {e.actor.username}
                    </Link>
                    <span>· {formatRelative(e.created_at)}</span>
                    {gbViewerIsOwner && !e.is_public && (
                      <span className="text-[var(--magenta)] inline-flex items-center gap-1">
                        <EyeOff className="h-3 w-3" /> KUN SYNLIG FOR DEG
                      </span>
                    )}
                  </div>
                  <p className="font-fraunces text-[var(--cream)] leading-relaxed whitespace-pre-wrap">{e.body}</p>
                  {gbViewerIsOwner && (
                    <div className="mt-3 flex items-center gap-4 font-meta text-xs">
                      <button onClick={() => toggleGuestbookVisibility(e.id)}
                        className="text-[var(--cream-soft)] hover:text-[var(--magenta)] inline-flex items-center gap-1.5"
                        data-testid={`guestbook-toggle-${e.id}`}>
                        {e.is_public ? <><EyeOff className="h-3 w-3" /> {T("ui.hide_for_others")}</>
                                     : <><Eye className="h-3 w-3" /> {T("ui.show_for_others")}</>}
                      </button>
                      <button onClick={() => deleteGuestbookEntry(e.id)}
                        className="text-[var(--cream-soft)] hover:text-[var(--magenta)] inline-flex items-center gap-1.5"
                        data-testid={`guestbook-delete-${e.id}`}>
                        <Trash2 className="h-3 w-3" /> Slett
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* GALLERI */}
      {tab === "galleri" && (
        <>
          {isMe && (
            <div className="flex items-center justify-between mb-5" data-testid="galleri-tab-header">
              <div className="font-meta text-[var(--cream-soft)]">
                {albums.length} {albums.length === 1 ? "album" : "album"}
              </div>
              <Link
                to="/albums"
                data-testid="profile-new-album-btn"
                className="px-4 py-2 bg-[var(--magenta)] text-black font-meta inline-flex items-center gap-2 hover:bg-[var(--cream)] transition-colors"
              >
                <FolderPlus className="h-3.5 w-3.5" /> {T("ui.new_album")}
              </Link>
            </div>
          )}
          {albums.length === 0 ? (
            isMe ? (
              <div className="panel-tight p-10 text-center">
                <ImageIcon className="h-10 w-10 text-[var(--cream-faint)] mx-auto mb-4 opacity-60" />
                <p className="font-fraunces italic text-2xl text-[var(--cream)] mb-2">{T("ui.no_albums")}</p>
                <p className="text-[var(--cream-soft)] text-sm mb-5">
                  Lag ditt første album og last opp bilder eller filmer.
                </p>
                <Link to="/albums" className="btn-magenta inline-flex items-center gap-2">
                  <FolderPlus className="h-3.5 w-3.5" /> Opprett album
                </Link>
              </div>
            ) : (
              <div className="font-fraunces italic text-[var(--cream-soft)]">{T("ui.no_albums")}</div>
            )
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {albums.map((a) => (
                <Link key={a.id} to={`/albums/${a.id}`} className="group">
                  <div className="aspect-[3/4] bg-[var(--noir-elev)] overflow-hidden ring-1 ring-white/5 group-hover:ring-[var(--magenta)]/40 transition-all">
                    {a.cover_image_id ? (
                      <AuthImage imageId={a.cover_image_id} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[var(--cream-faint)]"><ImageIcon className="h-10 w-10" /></div>
                    )}
                  </div>
                  <div className="pt-3">
                    <div className="font-fraunces italic text-lg text-[var(--cream)] truncate">{a.system_kind ? "★ " : ""}{a.title}</div>
                    <div className="font-meta">{a.image_count} bilder</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </>
      )}

      {/* FORUM (privat – kun eier) */}
      {/* GRUPPER (privat – kun eier) */}
      {tab === "grupper" && (
        groups.length === 0 ? (
          <div className="font-fraunces italic text-[var(--cream-soft)]">
            {T("ui.no_groups")} <Link to="/grupper" className="text-[var(--magenta)]">{T("ui.explore_groups")}</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {groups.map((g) => (
              <Link key={g.id} to={`/grupper/${g.id}`} className="editorial-card p-5">
                <div className="font-fraunces italic text-xl text-[var(--cream)]">{g.name}</div>
                <div className="font-meta mt-2">{T("ui.group_counts", { members: g.member_count, posts: g.post_count || 0 })}</div>
              </Link>
            ))}
          </div>
        )
      )}

      {/* NOVELLER */}
      {tab === "noveller" && (
        <div>
          {isMe && (
            <div className="mb-8">
              <Link to="/noveller/ny" className="btn-magenta inline-flex items-center" data-testid="profile-new-novelle">
                Skriv en novelle →
              </Link>
            </div>
          )}
          {noveller.length === 0 ? (
            <div className="font-fraunces italic text-[var(--cream-soft)]">
              {isMe ? T("ui.no_stories_me") : T("ui.no_stories_other", { name: profile.username })}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="profile-novelle-list">
              {noveller.map((n) => (
                <Link key={n.id} to={`/noveller/${n.id}`}
                  className="editorial-card p-5 hover:ring-1 hover:ring-[var(--magenta)]/40 transition-all">
                  <div className="flex justify-between items-start gap-2 mb-2">
                    <div className="font-fraunces italic text-xl text-[var(--cream)] leading-tight">{n.title}</div>
                    {n.token_cost > 0 ? (
                      <span className="font-meta text-[var(--magenta)] shrink-0">{n.token_cost}T</span>
                    ) : (
                      <span className="font-meta text-[10px] shrink-0">{T("ui.free")}</span>
                    )}
                  </div>
                  <p className="font-fraunces text-[var(--cream-soft)] line-clamp-3 mb-3">{n.teaser}</p>
                  <div className="font-meta flex items-center justify-between">
                    <span>{n.read_count} lest · {n.like_count} likes</span>
                    {n.rating_count > 0 && <span>★ {n.avg_rating.toFixed(1)} ({n.rating_count})</span>}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {/* FAVORITTER (privat – kun eier) */}
      {tab === "favoritter" && <MyFavoritesList />}

      {editing && (
        <EditProfileModal user={profile} onClose={()=>setEditing(false)} onSaved={() => { load(); refresh(); }} />
      )}
      {pinning && (
        <PinnedImagesModal currentIds={(pinned_images || []).map(i => i.id)} onClose={()=>setPinning(false)} onSaved={load} />
      )}
    </Layout>
  );
}
